/**
 * One chip stating what the AI reviewer did.
 *
 * Deliberately NOT collapsed into "AI reviewed: yes/no". The six states mean
 * six different things about how far to trust the score beside them, and a
 * boolean throws away the only part a reader needs:
 *
 *   completed    — it ran. The one state that earns the brand-purple chip.
 *   not_needed   — the rules found nothing ambiguous, so no call was made.
 *                  A POSITIVE outcome, styled neutrally. Never an empty state:
 *                  a reader who saw "AI reviewed" on the other source would
 *                  otherwise read silence here as a review that failed.
 *   not_enabled  — the reviewer is switched off for this source.
 *   unavailable  — it should have run and its answer never arrived.
 *   rejected     — it ran and its answer was discarded.
 *   failed       — it was called and errored.
 *
 * The last three are DEGRADED and say plainly that the score beside them is
 * rule-based only. They are AMBER, never red: a missing AI opinion does not
 * invalidate the deterministic checks, and this panel never blocks the page.
 *
 * There is no retry control, because no such action exists — the only way to
 * re-run validation is to re-ingest the company.
 */

import Tooltip from '../../ui/Tooltip';
import type { ValidationSummary } from '../../../types/dataQuality';
import { AI_STATUS_TOKENS } from './dqTokens';

export interface AiStatusChipProps {
  summary: ValidationSummary;
  /** `full` adds the whole sentence; `chip` is the compact badge only. */
  variant?: 'chip' | 'full';
}

export default function AiStatusChip({ summary, variant = 'chip' }: AiStatusChipProps) {
  const status = summary.ai_status;
  if (!status) return null;

  const token = AI_STATUS_TOKENS[status];
  if (!token) return null;

  const Icon = token.icon;
  // Prefer the model over the provider — it is the more informative of the two.
  const attribution =
    token.tone === 'reviewed' ? summary.ai_model || summary.ai_provider : null;

  if (variant === 'full') {
    return (
      <div
        className={`flex items-start gap-2 rounded-lg px-3 py-2 text-xs leading-relaxed ${token.chip}`}
      >
        <Icon className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span>
          {token.message}
          {attribution && <span className="ml-1 opacity-80">({attribution})</span>}
        </span>
      </div>
    );
  }

  return (
    <Tooltip content={token.message} position="top">
      <span
        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs ${token.chip}`}
      >
        <Icon className="h-3 w-3 shrink-0" aria-hidden="true" />
        {token.label}
        {attribution && <span className="opacity-70">· {attribution}</span>}
      </span>
    </Tooltip>
  );
}
